import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import AdminLayout from "@/components/AdminLayout";
import { http, API } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";

export default function AdminSectorReports() {
  const [reports, setReports] = useState([]);
  const [gen, setGen] = useState({ sector_code: "FASHION", quarter: "" });
  const [editing, setEditing] = useState(null);
  const [body, setBody] = useState({ en: "", it: "" });

  const load = () => http.get("/admin/sector-reports").then((r) => setReports(r.data.reports || []));
  useEffect(() => { load(); }, []);

  const generate = async () => {
    try {
      const { data } = await http.post("/admin/sector-reports/generate", gen);
      toast.success(`Generated draft: ${data.report.slug}`);
      load();
      setEditing(data.report.slug);
      setBody({ en: data.report.body.en, it: data.report.body.it });
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const save = async () => {
    try { await http.put(`/admin/sector-reports/${editing}`, { body_en: body.en, body_it: body.it }); toast.success("Saved"); load(); }
    catch { toast.error("Save failed"); }
  };

  const approve = async () => {
    try { await http.post(`/admin/sector-reports/${editing}/approve`); toast.success("Published"); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const sendQuarterly = async () => {
    try { const { data } = await http.post("/admin/digests/quarterly"); toast.success(`Sent ${data.sent} emails (or mocked)`); }
    catch { toast.error("Failed"); }
  };
  const sendWeekly = async () => {
    try { const { data } = await http.post("/admin/digests/weekly"); toast.success(`Sent ${data.sent} weekly digests (or mocked)`); }
    catch { toast.error("Failed"); }
  };

  return (
    <AdminLayout>
      <div className="bp-container py-8">
        <div className="flex items-center justify-between mb-6">
          <h1 className="font-serif text-4xl">Sector reports</h1>
          <div className="flex gap-2">
            <Button variant="outline" className="rounded-none" onClick={sendWeekly} data-testid="btn-send-weekly">Send weekly digests</Button>
            <Button variant="outline" className="rounded-none" onClick={sendQuarterly} data-testid="btn-send-quarterly">Send quarterly digests</Button>
          </div>
        </div>

        <div className="p-5 bg-white border mb-8 flex items-end gap-3 flex-wrap" style={{ borderColor: "var(--bp-border)" }}>
          <div>
            <label className="text-xs uppercase tracking-widest">Sector</label>
            <select value={gen.sector_code} onChange={(e) => setGen({ ...gen, sector_code: e.target.value })} className="w-full border p-2 bg-white" style={{ borderColor: "var(--bp-border)" }} data-testid="gen-sector">
              <option value="FASHION">FASHION</option><option value="FURNITURE">FURNITURE</option>
            </select>
          </div>
          <div>
            <label className="text-xs uppercase tracking-widest">Quarter (optional)</label>
            <input value={gen.quarter} onChange={(e) => setGen({ ...gen, quarter: e.target.value })} placeholder="2026-Q1" className="border p-2 bg-white" style={{ borderColor: "var(--bp-border)" }} data-testid="gen-quarter" />
          </div>
          <Button onClick={generate} className="rounded-none" data-testid="gen-submit">Generate report</Button>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div className="bg-white border" style={{ borderColor: "var(--bp-border)" }}>
            <div className="p-4 border-b uppercase text-xs tracking-widest" style={{ borderColor: "var(--bp-border)", color: "var(--bp-text-muted)" }}>All reports</div>
            <ul className="divide-y" style={{ borderColor: "var(--bp-border)" }}>
              {reports.map((r) => (
                <li key={r.slug} className="p-4 flex items-center justify-between" data-testid={`report-list-${r.slug}`}>
                  <div>
                    <div className="font-serif">{r.slug}</div>
                    <div className="text-xs font-mono" style={{ color: "var(--bp-text-muted)" }}>{r.status} · {(r.generated_at || "").slice(0, 10)}</div>
                  </div>
                  <div className="flex gap-2 items-center">
                    {r.status === "PUBLISHED" && <a href={`${API}/sector-reports/${r.slug}.pdf`} target="_blank" rel="noopener noreferrer" className="text-xs bp-link">PDF</a>}
                    <button onClick={() => { setEditing(r.slug); setBody({ en: r.body?.en || "", it: r.body?.it || "" }); }} className="text-sm underline" data-testid={`edit-report-${r.slug}`}>Edit →</button>
                  </div>
                </li>
              ))}
              {reports.length === 0 && <li className="p-4 text-sm" style={{ color: "var(--bp-text-muted)" }}>No reports yet.</li>}
            </ul>
          </div>

          {editing && (
            <div className="bg-white border p-5" style={{ borderColor: "var(--bp-border)" }}>
              <div className="uppercase text-xs tracking-widest mb-3" style={{ color: "var(--bp-text-muted)" }}>Editing {editing}</div>
              <label className="text-xs uppercase tracking-widest">Body EN</label>
              <Textarea rows={10} value={body.en} onChange={(e) => setBody({ ...body, en: e.target.value })} data-testid="report-body-en" />
              <label className="text-xs uppercase tracking-widest mt-3 block">Body IT</label>
              <Textarea rows={10} value={body.it} onChange={(e) => setBody({ ...body, it: e.target.value })} data-testid="report-body-it" />
              <div className="mt-4 flex gap-2">
                <Button onClick={save} className="rounded-none" data-testid="report-save">Save</Button>
                <Button onClick={approve} variant="outline" className="rounded-none" data-testid="report-approve">Approve &amp; publish</Button>
                {reports.find((r) => r.slug === editing)?.status === "PUBLISHED" && (
                  <Link to={`/reports/${editing}`} className="text-sm bp-link self-center ml-auto">View public →</Link>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </AdminLayout>
  );
}
